import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ComboClearButton } from '@/components/ui/ComboClearButton/ComboClearButton';
import { Badge } from '@/components/ui/Badge/Badge';
import { ContentPasteSearchIcon } from '@/components/ui/Icons';
import { api } from '@/api/client';
import type { Corrector } from '@/api/client';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import '@/components/TecheditorCombo/TecheditorCombo.css';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 25;
const MIN_SEARCH_LENGTH = 2;

export type CorrectorValueItem = { id: string; name: string; email?: string | null };

interface CorrectorComboProps {
  value: CorrectorValueItem[];
  onChange: (correctors: CorrectorValueItem[]) => void;
  placeholder?: string;
  disabled?: boolean;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  debounceFlushKey?: string | number;
}

type Option = Corrector | { id: string; name: string };

export function CorrectorCombo({
  value,
  onChange,
  placeholder = 'Корректор',
  disabled,
  containerRef,
  debounceFlushKey,
}: CorrectorComboProps) {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [page, setPage] = useState(0);
  const [accumulatedItems, setAccumulatedItems] = useState<Option[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const containerRefLocal = useRef<HTMLDivElement>(null);
  const dropdownWrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const resolvedContainerRef = containerRef ?? containerRefLocal;
  const dropdownStyle = useDropdownPosition(resolvedContainerRef, open, { dropdownRef: dropdownWrapRef });

  const searchQuery = inputValue.trim();
  const debouncedSearch = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS, debounceFlushKey);
  const { data: searchResponse, isFetching, isPlaceholderData } = useQuery({
    queryKey: ['correctors', debouncedSearch, page],
    queryFn: () => api.correctors.list(debouncedSearch, { limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    enabled: debouncedSearch.trim().length >= MIN_SEARCH_LENGTH,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!searchResponse || isPlaceholderData) return;
    if (page === 0) {
      setAccumulatedItems(searchResponse.items);
      setHasMore(searchResponse.hasMore);
    } else {
      setAccumulatedItems((prev) => [...prev, ...searchResponse.items]);
      setHasMore(searchResponse.hasMore);
    }
  }, [searchResponse, page, debouncedSearch, isPlaceholderData]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const valueIds = value.map((r) => r.id);
  const filtered = accumulatedItems.filter((r) => !valueIds.includes(r.id));
  const options: Option[] = filtered;

  const handleLoadMore = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPage((p) => p + 1);
  }, []);

  const selectOption = useCallback(
    (opt: Option) => {
      const email = 'email' in opt ? (opt as Corrector).email : undefined;
      onChange([...value, { id: opt.id, name: opt.name, email }]);
      setInputValue('');
      setOpen(false);
    },
    [value, onChange]
  );

  const removeCorrector = (item: CorrectorValueItem) => {
    onChange(value.filter((r) => r.id !== item.id));
  };

  useEffect(() => {
    setHighlightIndex(0);
  }, [options.length]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.children[highlightIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex, open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if ((e.key === 'ArrowDown' || e.key === 'Enter') && searchQuery.length >= MIN_SEARCH_LENGTH) setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[highlightIndex];
      if (opt) selectOption(opt);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleBlur = () => {
    setOpen(false);
  };

  return (
    <div ref={resolvedContainerRef as React.RefObject<HTMLDivElement>} className="techeditor-combo">
      <div className={`techeditor-combo__field ${value.length > 0 && !disabled ? 'techeditor-combo__field_has-clear' : ''}`}>
        <div className="techeditor-combo__chips">
          {value.map((r) => (
            <Badge key={r.id} variant="contractor">
              {r.name}
              {!disabled && (
                <button
                  type="button"
                  className="badge__remove"
                  onClick={() => removeCorrector(r)}
                  aria-label={`Удалить ${r.name}`}
                >
                  ×
                </button>
              )}
            </Badge>
          ))}
        </div>
        <div className="techeditor-combo__input-wrap">
          <input
            className={`techeditor-combo__input ${isFetching ? 'techeditor-combo__input_loading' : ''}`}
            value={inputValue}
            onChange={(e) => {
              const v = e.target.value;
              setInputValue(v);
              setOpen(v.trim().length > 0);
            }}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={disabled ? '' : placeholder}
            disabled={disabled}
            autoComplete="off"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-controls="corrector-combo-list"
            aria-activedescendant={open ? `corrector-opt-${highlightIndex}` : undefined}
          />
          {isFetching && <span className="techeditor-combo__loading">…</span>}
        </div>
        {value.length > 0 && !disabled && (
          <ComboClearButton onClick={() => onChange([])} />
        )}
      </div>
      {open && searchQuery.length > 0 &&
        createPortal(
          <div
            ref={dropdownWrapRef}
            className="techeditor-combo__dropdown-wrap"
            data-combo-dropdown
            style={dropdownStyle}
          >
            {searchQuery.length < MIN_SEARCH_LENGTH ? (
              <div className="techeditor-combo__hint">Введите минимум 2 символа для поиска</div>
            ) : options.length === 0 && isFetching ? (
              <div className="techeditor-combo__hint">Загрузка…</div>
            ) : options.length === 0 ? (
              <div className="techeditor-combo__hint">Нет результатов</div>
            ) : (
            <>
            <OverlayScrollArea
              contentRef={listRef}
              element="ul"
              id="corrector-combo-list"
              className="techeditor-combo__dropdown"
              role="listbox"
            >
              {options.map((opt, i) => (
                <li
                  key={opt.id}
                  id={`corrector-opt-${i}`}
                  role="option"
                  aria-selected={i === highlightIndex}
                  className={`techeditor-combo__option ${i === highlightIndex ? 'techeditor-combo__option_highlight' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectOption(opt);
                  }}
                >
                  {opt.name}
                </li>
              ))}
            </OverlayScrollArea>
            {hasMore && (
              <button
                type="button"
                className="techeditor-combo__load-more"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleLoadMore(e);
                }}
              >
                <ContentPasteSearchIcon size={18} />
                Загрузить ещё
              </button>
            )}
            </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
